import React from 'react';
import Button, { ButtonAsButtonProps } from './Button';

// SubmitButton — load-bearing wrapper used across the booking flow
// (Continue / Skip / Pay now / Update / Recompute / Lock day / Unlock day) and
// across many admin modals. The public API is preserved verbatim so every
// existing call site keeps working without edits:
//
//   <SubmitButton red isSubmitting onClick={…}>Update</SubmitButton>
//
// Internally we now render the canonical <Button> primitive. Variants/sizes
// are derived from the legacy props:
//   red === true            → variant="danger"   (else "primary")
//   isSubmitting === true   → loading={true}     (Button shows spinner +
//                                                 sets aria-busy)
//
// The legacy `submitButton` (and `red`) classNames stay attached because
// many page-level SCSS rules (collection-delivery, payment, removals,
// confirmation, oxford-packages, admin-tables, admin-partners, dpd-shipping,
// logistics-modal, auth-card, user-dashboard) target `button.submitButton`
// for layout/sizing inside specific contexts. Removing the className would
// break those page overrides; keeping it preserves the strangler-fig
// coexistence (legacy visual rules continue to win on specificity until
// Phase 2.3 deletes the rules in `_utilities.scss` and the page-level
// overrides migrate to the new primitive).
//
// Spinner: previously rendered <Loading /> wrapped in a <div className="spinning">.
// Now the Button primitive renders its own .kk-button__spinner span which
// respects prefers-reduced-motion. The legacy `.loading-spinner` and
// `.spinning` rules in `_utilities.scss` had no external consumers
// (verified by grep) — safe to drop the wrapping div.

interface SubmitProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  isSubmitting?: boolean;
  red?: boolean;
}

export default function SubmitButton({
  children,
  isSubmitting,
  red,
  className,
  type,
  onClick,
  disabled,
  ...props
}: SubmitProps): JSX.Element {
  const legacyClassName = red ? 'submitButton red' : 'submitButton';
  const mergedClassName = className
    ? `${legacyClassName} ${className}`
    : legacyClassName;

  // The DOM `aria-expanded` attribute type allows the stringy "true" / "false"
  // forms (`Booleanish`); the <Button> primitive narrows to a strict boolean.
  // Forward the rest as a deliberately wider type to satisfy the discriminated
  // union without losing any pass-through props the original SubmitButton
  // accepted (id, name, data-testid, aria-label, form, formAction, etc.).
  const buttonProps = props as Partial<ButtonAsButtonProps>;

  return (
    <Button
      {...buttonProps}
      variant={red ? 'danger' : 'primary'}
      size="md"
      loading={isSubmitting || false}
      disabled={disabled || false}
      type={type || (onClick ? 'button' : 'submit')}
      onClick={onClick}
      className={mergedClassName}
    >
      {children}
    </Button>
  );
}
